import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { checkAdminCredentials } from './adminAuth';

const AdminPage = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [adminCredentials, setAdminCredentials] = useState({ username: '', password: '' });
  const [registrations, setRegistrations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLetter, setSelectedLetter] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);

  const toggleDropdown = (id) => {
    setDropdownOpen((prev) => (prev === id ? null : id));
  };

  useEffect(() => {
    // Check if admin is already logged in
    const loggedIn = localStorage.getItem('isAdmin');
    if (loggedIn) {
      setIsAdmin(true);
      fetchRegistrations();
    } else {
      setShowLoginPopup(true);
    }
  }, []);
  const BASE_URL = "https://bootcamp-registration.onrender.com";
  // const BASE_URL = "http://localhost:8000";

  const handleLogin = () => {
    const { username, password } = adminCredentials;

    // Validate admin credentials (replace with secure logic)
    if (checkAdminCredentials(username, password)) {
      localStorage.setItem('isAdmin', 'true');
      setIsAdmin(true);
      setShowLoginPopup(false);
      fetchRegistrations();
    } else {
      toast.error('Invalid credentials!');
    }
  };

  const fetchRegistrations = async () => {
    try {
      const response = await axios.get(BASE_URL + '/api/registrations'); // Replace with your backend endpoint
      setRegistrations(response.data);
    } catch (error) {
      console.error('Error fetching registrations:', error);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.post(BASE_URL +
        "/api/users/download",
        { users: filteredRegistrations },
        { responseType: "blob" } // Ensures the response is treated as a file
      );

      // Create a URL for the file and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "users.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading Excel file", error);
    }
  };

  const handleApproval = async (userId, status) => {
    setDropdownOpen(null); // Close the dropdown
    try {
      // Show a confirmation dialog
      const isConfirmed = window.confirm(
        `Are you sure you want to ${status.toLowerCase()} this user?`
      );

      if (!isConfirmed) {
        return; // Exit if the user cancels
      }

      await axios.put(BASE_URL + `/api/users/${userId}`, {
        status,
      });

      // Update the local user list with the new status
      setRegistrations((prevUsers) =>
        prevUsers.map((user) =>
          user._id === userId ? { ...user, status } : user
        )
      );

      toast.success(`User ${status}`);
    } catch (error) {
      console.error(`Error updating user status to ${status}`, error);
      toast.error(`Failed to ${status} user.`);
    }
  };

  const closeModal = () => {
    setSelectedLetter("");
    setIsModalOpen(false);
  };

  const filteredRegistrations = registrations.filter(reg =>
    (reg.firstName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (reg.lastName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (reg.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (reg.phone || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (reg.gender || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (reg.isStudentOrWorking || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (reg.trackAppliedFor || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (reg.city || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (reg.status || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (reg.hearAboutUs || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (reg.admissionNumber || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (showLoginPopup) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50">
        <div className="bg-white p-5 rounded shadow-md">
          <h2 className="text-xl font-bold mb-4">Admin Login</h2>
          <input
            type="text"
            placeholder="Username"
            className="border p-2 mb-2 w-full"
            onChange={(e) => setAdminCredentials({ ...adminCredentials, username: e.target.value })}
          />
          <input
            type="password"
            placeholder="Password"
            className="border p-2 mb-4 w-full"
            onChange={(e) => setAdminCredentials({ ...adminCredentials, password: e.target.value })}
          />
          <button
            onClick={handleLogin}
            className="bg-[#F20034] text-white px-4 py-2 rounded"
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5">
      <h1 className="text-2xl font-bold mb-4">Registration List</h1>
      <div className="flex gap-2 justify-between items-center mb-4">
        <input
          type="text"
          placeholder="Search by text"
          className="border w-full p-2"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button
          onClick={handleDownload}
          className="bg-[#F20034] text-white px-4 py-3 w-[180px] rounded-sm transition duration-200"
        >
          Download Excel
        </button>
      </div>

      {/* Table Container */}
      <div className="overflow-auto max-h-[650px] border border-gray-300">
        <table className="table-auto w-full border-collapse border border-gray-300">
          <thead className="bg-white sticky top-0 shadow-md z-10">
            <tr>
              <th className="border border-gray-300 px-4 py-2">S/N</th>
              <th className="border border-gray-300 px-4 py-2">Name</th>
              <th className="border border-gray-300 px-4 py-2">Email</th>
              <th className="border border-gray-300 px-4 py-2">Phone</th>
              <th className="border border-gray-300 px-4 py-2">Has Laptop</th>
              <th className="border border-gray-300 px-4 py-2">Gender</th>
              <th className="border border-gray-300 px-4 py-2">City</th>
              <th className="border border-gray-300 px-4 py-2">Reliable Internet Connection</th>
              <th className="border border-gray-300 px-4 py-2">Accessibility Needs</th>
              <th className="border border-gray-300 px-4 py-2">Student Or Working</th>
              <th className="border border-gray-300 px-4 py-2">Highest Education Level</th>
              <th className="border border-gray-300 px-4 py-2">Track Applied For</th>
              <th className="border border-gray-300 px-4 py-2">Admission Number</th>
              <th className="border border-gray-300 px-4 py-2">Has Paid</th>
              <th className="border border-gray-300 px-4 py-2">Profile Image Link</th>
              <th className="border border-gray-300 px-4 py-2">Status</th>
              <th className="border border-gray-300 px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {registrations.length === 0 ?
              <tr className='flex justify-center items-center'>
                <td className='text-center'>loading...</td>
              </tr>
              : filteredRegistrations.map((reg, index) => (
                <tr key={index}>
                  <td className="border border-gray-300 px-4 py-2">{index + 1}</td>
                  <td className="border border-gray-300 px-4 py-2">{reg.firstName + " " + reg.lastName}</td>
                  <td className="border border-gray-300 px-4 py-2">{reg.email}</td>
                  <td className="border border-gray-300 px-4 py-2">{reg.phone}</td>
                  <td className="border border-gray-300 px-4 py-2">{reg.hasLaptop}</td>
                  <td className="border border-gray-300 px-4 py-2">{reg.gender}</td>
                  <td className="border border-gray-300 px-4 py-2">{reg.city}</td>
                  <td className="border border-gray-300 px-4 py-2">{reg.reliableInternetConnection}</td>
                  <td className="border border-gray-300 px-4 py-2">{reg.accessibilityNeeds}</td>
                  <td className="border border-gray-300 px-4 py-2">{reg.isStudentOrWorking}</td>
                  <td className="border border-gray-300 px-4 py-2">{reg.highestEducationLevel}</td>
                  <td className="border border-gray-300 px-4 py-2">{reg.trackAppliedFor}</td>
                  <td className="border border-gray-300 px-4 py-2">{reg.admissionNumber}</td>
                  <td className="border border-gray-300 px-4 py-2">{reg.isPaid ? 'Yes' : 'No'}</td>
                  <td className="border border-gray-300 px-4 py-2">{reg.profileImgLink}</td>
                  <td className="border border-gray-300 px-4 py-2">{reg.status}</td>
                  <td className="border border-gray-300 px-4 py-2">
                    <div className="relative">
                      {/* Dropdown Trigger */}
                      <button
                        onClick={() => toggleDropdown(reg._id)} // Toggle dropdown visibility
                        className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition duration-200"
                      >
                        Actions
                      </button>

                      {/* Dropdown Menu */}
                      {dropdownOpen === reg._id && ( // Show dropdown only for the selected row
                        <div className="absolute [left:-10rem] [top:-6rem] z-10 bg-white shadow-lg rounded-lg mt-2 w-40">
                          <button
                            onClick={() => handleApproval(reg._id, "Approved")}
                            className="block w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-100"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleApproval(reg._id, "Declined")}
                            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-100"
                          >
                            Decline
                          </button>
                        </div>
                      )}
                    </div>
                  </td>

                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white w-11/12 md:w-2/3 lg:w-1/2 max-h-[80vh] p-6 rounded-lg shadow-lg overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">
              Motivation Letter
            </h2>
            <p className="text-gray-700 whitespace-pre-wrap">{selectedLetter}</p>
            <div className="mt-6 text-right">
              <button
                onClick={closeModal}
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminPage;
